import { useEffect, useRef, useState } from "react";
import { checkForNewBuild, isBuildCheckEnabled, isUpdateAvailable, reloadOnce, subscribeToBuildUpdate, } from "@/utils/buildCheck";
export const BUILD_CHECK_INTERVAL_MS = 10 * 60 * 1000;
export const BUILD_CHECK_THROTTLE_MS = 30 * 1000;
const noop = () => { };
/**
 * Checks for a new deploy once on start, when the tab becomes visible, regains focus, comes back online, is restored
 * from the back/forward cache, and every 10 minutes.
 * Checks are throttled to one per 30s; the interval skips hidden tabs. Does nothing for local builds. Returns a
 * cleanup function.
 */
export const startBuildWatcher = () => {
    if (!isBuildCheckEnabled())
        return noop;
    let lastCheckAt = 0;
    const check = () => {
        if (isUpdateAvailable())
            return;
        const now = Date.now();
        if (lastCheckAt && now - lastCheckAt < BUILD_CHECK_THROTTLE_MS)
            return;
        lastCheckAt = now;
        checkForNewBuild().catch(noop);
    };
    const onVisibilityChange = () => {
        if (document.visibilityState === "visible")
            check();
    };
    const onPageShow = (event) => {
        if (event.persisted)
            check();
    };
    const onInterval = () => {
        if (document.visibilityState !== "hidden")
            check();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    window.addEventListener("pageshow", onPageShow);
    const intervalId = window.setInterval(onInterval, BUILD_CHECK_INTERVAL_MS);
    check();
    return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("focus", check);
        window.removeEventListener("online", check);
        window.removeEventListener("pageshow", onPageShow);
        window.clearInterval(intervalId);
    };
};
/** Runs the build watcher while mounted and reports whether a newer build has been deployed. */
export const useBuildCheck = () => {
    const [updateAvailable, setUpdateAvailable] = useState(isUpdateAvailable);
    useEffect(() => {
        const unsubscribe = subscribeToBuildUpdate(() => setUpdateAvailable(true));
        const stopWatcher = startBuildWatcher();
        // an update may have been detected (e.g. by a 400 response) before this hook subscribed
        if (isUpdateAvailable())
            setUpdateAvailable(true);
        return () => {
            unsubscribe();
            stopWatcher();
        };
    }, []);
    return { updateAvailable };
};
/**
 * Full reload on a route change (never on first mount) while an update is available: the user is leaving the page
 * anyway, so no open form is lost. Goes through `reloadOnce`, so a rolling restart cannot cause a reload loop; if the
 * reload is refused, nothing happens and the banner stays up.
 */
export const useReloadOnRouteChange = (pathname, updateAvailable) => {
    const previousPathnameRef = useRef(pathname);
    useEffect(() => {
        if (previousPathnameRef.current === pathname)
            return;
        previousPathnameRef.current = pathname;
        if (updateAvailable)
            reloadOnce();
    }, [pathname, updateAvailable]);
};
export default useBuildCheck;
